import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/AuthContext';
import Layout from './components/Layout';
import UpgradeGate from './components/UpgradeGate';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Receipts from './pages/Receipts';
import Budgets from './pages/Budgets';
import TaxReports from './pages/TaxReports';
import Documents from './pages/Documents';
import Insights from './pages/Insights';
import Invoices from './pages/Invoices';
import Mileage from './pages/Mileage';
import Bills from './pages/Bills';
import Settings from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import OcrAccuracy from './pages/OcrAccuracy';
import Employees from './pages/Employees';
import AccountantPortal from './pages/AccountantPortal';
import HomePage from './pages/HomePage';
import LegalPage from './pages/LegalPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30000, retry: 1 } }
});

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

// Wrapper that gates pages by plan — admins always pass
function PlanGate({ feature, requiredPlan, children }) {
  const { user } = useAuth();
  if (user?.role === 'admin') return children;
  const userPlan = user?.plan || 'individual';
  return (
    <UpgradeGate feature={feature} requiredPlan={requiredPlan} userPlan={userPlan}>
      {children}
    </UpgradeGate>
  );
}

function HomeOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (user) return <Navigate to="/app/dashboard" replace />;
  return <HomePage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeOrDashboard />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/accountant/:token" element={<AccountantPortal />} />
      <Route path="/privacy" element={<LegalPage pageKey="privacy" />} />
      <Route path="/terms" element={<LegalPage pageKey="terms" />} />
      <Route path="/cookie-policy" element={<LegalPage pageKey="cookie-policy" />} />
      <Route path="/security" element={<LegalPage pageKey="security" />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="receipts" element={<Receipts />} />
        <Route path="ocr-accuracy" element={<OcrAccuracy />} />
        <Route path="budgets" element={<PlanGate feature="Budgets" requiredPlan="basic"><Budgets /></PlanGate>} />
        <Route path="tax-reports" element={<PlanGate feature="Tax Reports" requiredPlan="basic"><TaxReports /></PlanGate>} />
        <Route path="documents" element={<Documents />} />
        <Route path="insights" element={<PlanGate feature="AI Insights" requiredPlan="basic"><Insights /></PlanGate>} />
        <Route path="invoices" element={<PlanGate feature="Invoices" requiredPlan="standard"><Invoices /></PlanGate>} />
        <Route path="mileage" element={<PlanGate feature="Mileage Tracking" requiredPlan="basic"><Mileage /></PlanGate>} />
        <Route path="bills" element={<PlanGate feature="Bills & Subscriptions" requiredPlan="basic"><Bills /></PlanGate>} />
        <Route path="employees" element={<PlanGate feature="Employees" requiredPlan="standard"><Employees /></PlanGate>} />
        <Route path="settings" element={<Settings />} />
        <Route path="admin" element={<AdminDashboard />} />
      </Route>
      {/* Legacy routes redirect to /app/* */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/receipts" element={<Navigate to="/app/receipts" replace />} />
      <Route path="/ocr-accuracy" element={<Navigate to="/app/ocr-accuracy" replace />} />
      <Route path="/budgets" element={<Navigate to="/app/budgets" replace />} />
      <Route path="/tax-reports" element={<Navigate to="/app/tax-reports" replace />} />
      <Route path="/documents" element={<Navigate to="/app/documents" replace />} />
      <Route path="/insights" element={<Navigate to="/app/insights" replace />} />
      <Route path="/invoices" element={<Navigate to="/app/invoices" replace />} />
      <Route path="/mileage" element={<Navigate to="/app/mileage" replace />} />
      <Route path="/bills" element={<Navigate to="/app/bills" replace />} />
      <Route path="/employees" element={<Navigate to="/app/employees" replace />} />
      <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
      <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
